import React, { useState } from "react";
import {
  KeyRound, Loader2, Copy, Check, ExternalLink, ChevronDown, ChevronUp,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";

export default function AdminResetLinkGenerator() {
  const { lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [ttl, setTtl] = useState(60);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const tr = (ca, es) => (lang === "es" ? es : ca);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post("/admin/users/generate-reset-link",
        { email, ttl_minutes: Number(ttl) });
      setResult(r.data);
      toast.success(tr("Enllaç generat", "Enlace generado"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  const copyToClipboard = async () => {
    if (!result?.reset_url) return;
    try {
      await navigator.clipboard.writeText(result.reset_url);
      setCopied(true);
      toast.success(tr("Copiat al porta-retalls", "Copiado al portapapeles"));
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error(tr("No s'ha pogut copiar", "No se ha podido copiar"));
    }
  };

  const reset = () => {
    setEmail("");
    setResult(null);
    setCopied(false);
  };

  return (
    <div className="card-surface p-5" data-testid="admin-reset-generator">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-2 text-left"
        data-testid="admin-reset-generator-toggle">
        <div className="flex items-center gap-2">
          <KeyRound size={14} className="text-amber-400" />
          <h2 className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
            {tr("Generar enllaç de reset manual",
                "Generar enlace de reset manual")}
          </h2>
        </div>
        {open ? <ChevronUp size={14} className="text-slate-500" />
              : <ChevronDown size={14} className="text-slate-500" />}
      </button>
      {open && (
        <div className="mt-4 space-y-4">
          <p className="text-xs text-slate-500 leading-relaxed">
            {tr(
              "Genera un enllaç de restabliment de contrasenya sense enviar email. Útil per a suport quan l'usuari no rep l'email automàtic.",
              "Genera un enlace de restablecimiento de contraseña sin enviar email. Útil para soporte cuando el usuario no recibe el email automático."
            )}
          </p>

          {!result ? (
            <form onSubmit={submit} className="space-y-3"
                  data-testid="admin-reset-form">
              <div>
                <Label className="text-[11px] text-slate-400">Email</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="user@example.com"
                  className="h-9 bg-white/5 border-white/10 text-white"
                  data-testid="admin-reset-email" />
              </div>
              <div>
                <Label className="text-[11px] text-slate-400">
                  {tr("Validesa (minuts)", "Validez (minutos)")}
                </Label>
                <div className="flex gap-1.5 flex-wrap">
                  {[30, 60, 240, 1440].map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setTtl(m)}
                      className={`px-2.5 py-1 rounded-md text-xs border ${
                        ttl === m
                          ? "bg-amber-500/20 border-amber-500/50 text-amber-100"
                          : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"
                      }`}
                      data-testid={`admin-reset-ttl-${m}`}>
                      {m < 60 ? `${m}m`
                        : m < 1440 ? `${m / 60}h`
                        : `${m / 60 / 24}d`}
                    </button>
                  ))}
                  <Input
                    type="number"
                    min={5}
                    max={1440}
                    value={ttl}
                    onChange={(e) => setTtl(e.target.value)}
                    className="h-7 w-20 text-xs bg-white/5 border-white/10 text-white"
                    data-testid="admin-reset-ttl-custom" />
                </div>
              </div>
              <Button
                type="submit"
                disabled={busy || !email}
                className="bg-amber-500 hover:bg-amber-600 text-black font-semibold"
                data-testid="admin-reset-submit">
                {busy ? <Loader2 size={14} className="mr-2 animate-spin" />
                  : <KeyRound size={14} className="mr-2" />}
                {tr("Generar enllaç", "Generar enlace")}
              </Button>
            </form>
          ) : (
            <div className="rounded-lg bg-amber-500/5 border border-amber-500/30 p-4 space-y-3"
                 data-testid="admin-reset-result">
              <div className="flex items-center gap-2 text-xs text-amber-200">
                <Check size={14} />
                <span>
                  {tr(`Enllaç vàlid ${result.ttl_minutes} minuts per `,
                      `Enlace válido ${result.ttl_minutes} minutos para `)}
                  <strong className="text-amber-100">{result.email}</strong>
                </span>
              </div>
              <div className="rounded bg-black/40 p-2.5 font-mono text-[10px] text-slate-300 break-all">
                {result.reset_url}
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={copyToClipboard}
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-black font-semibold"
                  data-testid="admin-reset-copy">
                  {copied ? <Check size={14} className="mr-2" />
                    : <Copy size={14} className="mr-2" />}
                  {copied ? tr("Copiat!", "¡Copiado!")
                    : tr("Copia l'enllaç", "Copiar enlace")}
                </Button>
                <Button
                  onClick={() => window.open(result.reset_url, "_blank")}
                  variant="outline"
                  className="bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"
                  data-testid="admin-reset-open">
                  <ExternalLink size={14} />
                </Button>
                <Button
                  onClick={reset}
                  variant="outline"
                  className="bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"
                  data-testid="admin-reset-new">
                  {tr("Nou", "Nuevo")}
                </Button>
              </div>
              <p className="text-[10px] text-slate-500 italic">
                {tr(
                  "Envia aquest enllaç al usuari per qualsevol canal segur (WhatsApp, SMS, etc). Caduca automàticament.",
                  "Envía este enlace al usuario por cualquier canal seguro (WhatsApp, SMS, etc). Caduca automáticamente."
                )}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
